import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Carousel, Button } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right.svg';
import AnnouncementOne from '@assets/images/announcement/announcement-1.png';
import AnnouncementTwo from '@assets/images/announcement/announcement-2.png';
import AnnouncementThree from '@assets/images/announcement/announcement-3.png';
import AnnouncementFour from '@assets/images/announcement/announcement-4.png';
import AnnouncementFive from '@assets/images/announcement/announcement-5.png';
import { redirectToSubdomain } from '@components/OrganizationBoundary';
import {
  DEMO_SUBDOMAIN,
  DEMO_TOUR_MEDIA_VIEWER_ANNOUNCEMENT_ASSET_ID
} from '@config/constants';

import './AnnouncementModal.less';

export type AnnouncementModalProps = {
  onFinish: (isClose?: boolean) => void | Promise<void>;
  open: boolean;
};

export default function AnnouncementModal({
  open,
  onFinish
}: AnnouncementModalProps) {
  const carouselRef = useRef<CarouselRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const items = useMemo(() => {
    return [
      {
        label: 'Media Viewer 2.0',
        title: 'New media-first layout',
        image: AnnouncementOne,
        text: (
          <>
            The Media Viewer is now more focused on the asset itself. Panels
            stay out of the way by default and open only when needed, giving you
            more space to review, comment, and make decisions.
          </>
        )
      },
      {
        label: 'Media Viewer 2.0',
        title: (
          <>
            Floating comments for
            <br />
            precise feedback
          </>
        ),
        image: AnnouncementTwo,
        text: (
          <>
            Leave feedback exactly where it belongs. Add comments directly on
            the media with inline pop-ups, without opening a separate comments
            panel.
          </>
        )
      },
      {
        label: 'Media Viewer 2.0',
        title: 'Review faster with shortcuts',
        image: AnnouncementThree,
        text: (
          <>
            Use keyboard shortcuts to quickly switch between tools, open panels,
            and navigate feedback while reviewing media.
          </>
        )
      },
      {
        label: 'Media Viewer 2.0',
        title: 'Copy comments',
        image: AnnouncementFour,
        text: (
          <>
            Reuse feedback from one asset on another. Copy comments from any
            media and paste them onto another version or related asset.
          </>
        )
      },
      {
        label: 'Media Viewer 2.0',
        title: 'Compare assets',
        image: AnnouncementFive,
        text: (
          <>
            Want to see how versions compare? Watch
            <br /> videos in unison, compare images, and leave
            <br /> comments on files side-by-side.
          </>
        )
      }
    ];
  }, []);

  const dots = useMemo(() => new Array(items.length).fill(''), [items]);

  const onCancel = useCallback(() => {
    onFinish(true);
    setActiveSlide(0);
  }, [onFinish]);

  const goNext = useCallback(() => {
    carouselRef.current?.next();
  }, []);

  const goPrev = useCallback(() => {
    carouselRef.current?.prev();
  }, []);

  const goTo = useCallback((index: number) => {
    carouselRef.current?.goTo(index);
  }, []);

  const onStartTour = useCallback(async () => {
    try {
      setIsLoading(true);
      await onFinish(false);
    } finally {
      setIsLoading(false);
      redirectToSubdomain(
        DEMO_SUBDOMAIN,
        `media-viewer/${DEMO_TOUR_MEDIA_VIEWER_ANNOUNCEMENT_ASSET_ID}`,
        [{ name: 'startTour', value: 'announcement-media-viewer' }]
      );
    }
  }, [onFinish]);

  return (
    <Modal
      open={open}
      footer={null}
      centered={true}
      closeIcon={<CrossSvg />}
      destroyOnClose
      width={434}
      maskClosable={false}
      onCancel={onCancel}
      className="announcement-modal"
    >
      <div className="modal_container">
        <Carousel
          dots={false}
          ref={carouselRef}
          afterChange={(index) => setActiveSlide(index)}
        >
          {items.map((item, index) => (
            <div key={index} className="announcement-modal_block">
              <div className="announcement-modal_image">
                <img src={item.image} alt="Announcement" />
              </div>
              {item.label && (
                <div className="announcement-modal_label">{item.label}</div>
              )}
              <div className="announcement-modal_title">{item.title}</div>
              <div className="announcement-modal_text">{item.text}</div>
            </div>
          ))}
        </Carousel>
        <div className="announcement-modal_bottom">
          <div className="announcement-modal_controls">
            <Button
              type="text"
              icon={<ArrowLeftSvg />}
              onClick={goPrev}
              disabled={activeSlide === 0}
              size="small"
            />
            <div className="announcement-modal_dots">
              {dots.map((_item, index) => (
                <div
                  key={index}
                  className={classNames('announcement-modal_dots-item', {
                    active: index === activeSlide
                  })}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
            <Button
              type="text"
              icon={<ArrowRightSvg />}
              onClick={goNext}
              disabled={activeSlide === items.length - 1}
              size="small"
            />
          </div>
          <Button
            type="primary"
            onClick={onStartTour}
            loading={isLoading}
            style={{ minWidth: 175 }}
          >
            Start a quick tour
          </Button>
        </div>
      </div>
    </Modal>
  );
}
